import type { StorefrontAiActivity, StorefrontAiReply, StorefrontAiStreamEvent, StorefrontProductSummary } from '@erp/shared';
import { isAxiosError } from 'axios';
import { AlertTriangle, ArrowDown, Check, ChevronRight, Maximize2, Minimize2, Package, Plus, Send, ShoppingBag, Sparkles, Square, Tag, Trash2, TrendingUp, Truck, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { renderMarkdownLite } from '../../components/ui/MarkdownLite.js';
import { useCartStore } from '../../lib/cartStore.js';
import { formatMoney } from '../../lib/money.js';
import { assetUrl, publicApi, storefrontApiBase } from '../../lib/publicApi.js';

import { rememberSaving } from './cartSavings.js';
import { OriginalPrice, resolveSaving } from './discountDisplay.js';

interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  products?: StorefrontProductSummary[];
  orderNumber?: string;
  /** Client-only failure row — excluded from the history sent to the backend. */
  error?: boolean;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Markdown-lite for AI replies — the shared renderer (components/ui/MarkdownLite). */
function renderAiText(text: string): ReactNode {
  return renderMarkdownLite(text, 'chat');
}

/** Parse an SSE byte stream (data:-only frames) and yield each JSON event. */
async function* readSse(body: ReadableStream<Uint8Array>): AsyncGenerator<StorefrontAiStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try { yield JSON.parse(line.slice(6)) as StorefrontAiStreamEvent; } catch { /* skip malformed frame */ }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** Restore a persisted transcript (sessionStorage) — defensive against stale/foreign shapes. */
function restoreChat(key: string): ChatMsg[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(key) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return (parsed as ChatMsg[])
      .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && !m.error)
      .slice(-40);
  } catch {
    return [];
  }
}

/**
 * Floating AI shopping assistant (bottom-left). Customers chat in natural
 * language; the server-side assistant searches THIS store's catalog (only) and
 * returns product cards the shopper can add to cart. Dark "command-palette"
 * styling; expands to full screen. Rendered only when `site.aiAssistantEnabled`.
 */
export function StorefrontAiWidget(): JSX.Element {
  const { slug, site } = useStorefront();
  const { t, i18n } = useTranslation('cms');
  const add = useCartStore((s) => s.add);
  const navigate = useNavigate();

  const storageKey = `sf-ai-chat:${slug}`;
  const [open, setOpen] = useState(false);
  // Brief exit animation before the docked panel unmounts (see requestClose).
  const [closing, setClosing] = useState(false);
  const [full, setFull] = useState(false);
  // Transcript survives in-tab navigation (sessionStorage, per store).
  const [messages, setMessages] = useState<ChatMsg[]>(() => restoreChat(storageKey));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // Live SSE text of the in-flight reply (empty until the first delta).
  const [streamText, setStreamText] = useState('');
  // What the assistant is doing between answer rounds (status line under the dots).
  const [activity, setActivity] = useState<StorefrontAiActivity | null>(null);
  // Typewriter reveal for the buffered-JSON fallback path only (null = fully shown).
  const [revealChars, setRevealChars] = useState<number | null>(null);
  const [showNewReply, setShowNewReply] = useState(false);
  const [addedId, setAddedId] = useState<string | null>(null);
  const [nudge, setNudge] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Whether the shopper is scrolled near the bottom (autoscroll follows only then).
  const pinnedRef = useRef(true);
  // Bumped by clear-conversation so in-flight replies are dropped.
  const epochRef = useRef(0);
  // In-flight stream — aborted by the stop button and by clear-conversation.
  const abortRef = useRef<AbortController | null>(null);
  // Pending unmount timer for the docked close animation.
  const closeTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  // Open the panel (cancels any in-flight close).
  const openPanel = (): void => {
    window.clearTimeout(closeTimer.current);
    setClosing(false);
    setOpen(true);
  };

  // Close with a short exit animation (docked only); full-screen + reduced-motion
  // dismiss immediately since the exit isn't visible there.
  const requestClose = useCallback((): void => {
    if (full || prefersReducedMotion()) {
      setOpen(false);
      setFull(false);
      setClosing(false);
      return;
    }
    setClosing(true);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 180);
  }, [full]);

  // Persist the transcript (error rows are transient and excluded).
  useEffect(() => {
    try {
      const keep = messages.filter((m) => !m.error).slice(-40);
      if (keep.length > 0) sessionStorage.setItem(storageKey, JSON.stringify(keep));
      else sessionStorage.removeItem(storageKey);
    } catch { /* private mode / quota */ }
  }, [messages, storageKey]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth'): void => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior });
  };

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const pinned = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    pinnedRef.current = pinned;
    if (pinned) setShowNewReply(false);
  };

  // Smart autoscroll: follow new messages while pinned near the bottom (or right
  // after the shopper's own message); otherwise surface a "new reply" pill.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (pinnedRef.current || last?.role === 'user') {
      scrollToBottom();
      setShowNewReply(false);
    } else if (last?.role === 'assistant') {
      setShowNewReply(true);
    }
  }, [messages, loading]);

  // Typewriter tick: a few chars every 30ms, total reveal capped at ~1.5s.
  useEffect(() => {
    if (revealChars === null) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.error) { setRevealChars(null); return; }
    const len = last.content.length;
    if (revealChars >= len) { setRevealChars(null); return; }
    const step = Math.max(2, Math.ceil(len / 50));
    const id = window.setTimeout(() => setRevealChars((c) => (c === null ? null : Math.min(c + step, len))), 30);
    return () => window.clearTimeout(id);
  }, [revealChars, messages]);

  // Keep following the reveal / live stream while pinned.
  useEffect(() => {
    if (revealChars !== null && pinnedRef.current) scrollToBottom('auto');
  }, [revealChars]);
  useEffect(() => {
    if ((streamText || activity) && pinnedRef.current) scrollToBottom('auto');
  }, [streamText, activity]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 50);
    // The panel DOM remounts when toggling docked/full — restore the bottom pin.
    pinnedRef.current = true;
    scrollToBottom('auto');
  }, [open, full]);

  // One-time launcher nudge (~6s on first visit, per store; any launcher click dismisses).
  useEffect(() => {
    if (open) return;
    try {
      if (localStorage.getItem(`sf-ai-nudge:${slug}`)) return;
    } catch { return; }
    const id = window.setTimeout(() => setNudge(true), 6000);
    return () => window.clearTimeout(id);
  }, [open, slug]);

  const dismissNudge = (): void => {
    setNudge(false);
    try { localStorage.setItem(`sf-ai-nudge:${slug}`, '1'); } catch { /* private mode */ }
  };

  // Esc closes (full-screen first, then the panel); lock page scroll in full-screen.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { if (full) setFull(false); else requestClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, full, requestClose]);
  useEffect(() => {
    if (!full) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [full]);

  const money = (p: StorefrontProductSummary): string => formatMoney(p.price, { locale: i18n.language, currency: p.currencyCode });

  const errorMsg = (rateLimited: boolean): ChatMsg => ({
    role: 'assistant',
    error: true,
    content: rateLimited
      ? t('ai.rateLimited', { defaultValue: 'I am getting a lot of questions right now — please try again in a little while.' })
      : t('ai.error', { defaultValue: 'Sorry, I had trouble answering — please try again.' }),
  });

  /** Land the turn's outcome (or nothing) and reset the in-flight UI. */
  const finishTurn = (epoch: number, msg: ChatMsg | null): void => {
    if (epochRef.current !== epoch) return;
    if (msg) setMessages((m) => [...m, msg]);
    setLoading(false);
    setActivity(null);
    setStreamText('');
  };

  /** Buffered-JSON fallback (also what customer-mobile uses) — typewriter reveal keeps it lively. */
  const runJson = async (payload: { messages: Array<{ role: 'user' | 'assistant'; content: string }> }, epoch: number): Promise<void> => {
    try {
      // Same `?locale=` the streaming twin sends. Without it this path loses the
      // "reply in the customer's language" instruction AND the translated
      // catalogue the tools return — so a fallback would quietly answer in the
      // wrong language for the same question.
      const { data } = await publicApi().post<StorefrontAiReply>(
        `/${slug}/ai/chat?locale=${encodeURIComponent(i18n.language)}`,
        payload,
      );
      finishTurn(epoch, { role: 'assistant', content: data.reply, products: data.products, ...(data.orderNumber ? { orderNumber: data.orderNumber } : {}) });
      if (epochRef.current === epoch && data.reply && !prefersReducedMotion()) setRevealChars(0);
    } catch (err) {
      finishTurn(epoch, errorMsg(isAxiosError(err) && err.response?.status === 429));
    }
  };

  const runChat = async (next: ChatMsg[]): Promise<void> => {
    const epoch = epochRef.current;
    setLoading(true);
    setActivity(null);
    setStreamText('');
    // The backend caps history at 20 messages — send a bounded, user-first
    // window (error rows are client-only and excluded).
    const history = next.filter((m) => !m.error).slice(-20);
    if (history[0]?.role === 'assistant') history.shift();
    const payload = { messages: history.map((m) => ({ role: m.role, content: m.content })) };

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let streamed = '';
    let sawEvent = false;
    try {
      const lng = (i18n.language || 'en').split('-')[0] ?? 'en';
      const res = await fetch(`${storefrontApiBase}/${slug}/ai/chat/stream?locale=${encodeURIComponent(lng)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
        body: JSON.stringify(payload),
        signal: ctrl.signal,
      });
      if (res.status === 429) { finishTurn(epoch, errorMsg(true)); return; }
      if (!res.ok || !res.body || !(res.headers.get('content-type') ?? '').includes('text/event-stream')) {
        // Stream endpoint unavailable (older API, buffering proxy) — buffered fallback.
        await runJson(payload, epoch);
        return;
      }
      for await (const ev of readSse(res.body)) {
        if (epochRef.current !== epoch) { ctrl.abort(); return; }
        sawEvent = true;
        if (ev.type === 'delta') {
          streamed += ev.text;
          setActivity(null);
          setStreamText(streamed);
        } else if (ev.type === 'status') {
          setActivity(ev.status);
        } else if (ev.type === 'reset') {
          // The streamed text was a preliminary round ("let me check…") — drop it.
          streamed = '';
          setStreamText('');
        } else if (ev.type === 'done') {
          finishTurn(epoch, { role: 'assistant', content: ev.reply, products: ev.products, ...(ev.orderNumber ? { orderNumber: ev.orderNumber } : {}) });
          return;
        } else if (ev.type === 'error') {
          finishTurn(epoch, errorMsg(false));
          return;
        }
      }
      // Stream ended without a terminal event (connection dropped mid-answer):
      // keep whatever already streamed rather than losing the reply.
      finishTurn(epoch, streamed.trim() ? { role: 'assistant', content: streamed.trim() } : errorMsg(false));
    } catch {
      if (ctrl.signal.aborted) {
        // Stop button (or clear) — keep the partial answer if there is one.
        finishTurn(epoch, streamed.trim() ? { role: 'assistant', content: streamed.trim() } : null);
        return;
      }
      // Never re-POST once events flowed — the turn may have had side effects
      // (an order). Only a clean pre-stream failure falls back to JSON.
      if (!sawEvent) { await runJson(payload, epoch); return; }
      finishTurn(epoch, errorMsg(false));
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  };

  const stop = (): void => abortRef.current?.abort();

  const send = async (text: string): Promise<void> => {
    const msg = text.trim();
    if (!msg || loading) return;
    const next: ChatMsg[] = [...messages, { role: 'user', content: msg }];
    setRevealChars(null);
    setMessages(next);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    await runChat(next);
  };

  const retry = (): void => {
    if (loading) return;
    const next = messages.filter((m) => !m.error);
    setMessages(next);
    if (next[next.length - 1]?.role === 'user') void runChat(next);
  };

  const clearChat = (): void => {
    epochRef.current += 1;
    abortRef.current?.abort();
    setMessages([]);
    setLoading(false);
    setStreamText('');
    setActivity(null);
    setRevealChars(null);
    setShowNewReply(false);
    pinnedRef.current = true;
    try { sessionStorage.removeItem(storageKey); } catch { /* private mode */ }
  };

  const addToCart = (p: StorefrontProductSummary): void => {
    add(slug, { itemId: p.id, name: p.name, price: p.price, currencyCode: p.currencyCode, imageUrl: p.imageUrl, quantity: 1 });
    // The cart keeps only the charged price — carry the saving alongside it.
    rememberSaving(slug, { itemId: p.id, price: p.price }, resolveSaving(p));
    setAddedId(p.id);
    window.setTimeout(() => setAddedId((cur) => (cur === p.id ? null : cur)), 1200);
    toast.success(t('cart.addedTitle', { defaultValue: 'Added to cart' }), {
      description: p.name,
      action: { label: t('cart.viewCart', { defaultValue: 'View cart' }), onClick: () => navigate(`/s/${slug}/cart`) },
    });
  };

  const autoGrow = (el: HTMLTextAreaElement): void => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`; // cap at ~4 rows
  };

  // Starter prompts shown on the empty state — each seeds a useful shopping task.
  const suggestions: Array<{ icon: LucideIcon; label: string }> = [
    { icon: TrendingUp, label: t('ai.s1', { defaultValue: 'Show me your bestsellers' }) },
    { icon: Tag, label: t('ai.s2', { defaultValue: 'Cheapest notebooks' }) },
    { icon: Package, label: t('ai.s3', { defaultValue: 'Track my order' }) },
    { icon: Truck, label: t('ai.s4', { defaultValue: 'Delivery & payment' }) },
  ];

  // Contextual quick replies, chosen client-side from the reply shape.
  const chipsFor = (m: ChatMsg): string[] => {
    if (m.orderNumber) return [t('ai.chipTrack', { defaultValue: 'Track my order' })];
    if (m.products && m.products.length > 0) {
      return [t('ai.chipCheaper', { defaultValue: 'Show cheaper options' }), t('ai.chipMore', { defaultValue: 'More like these' })];
    }
    return [t('ai.chipPopular', { defaultValue: "What's popular?" }), t('ai.chipDeals', { defaultValue: 'Any deals?' })];
  };

  // ---- Launcher (collapsed) ----
  if (!open) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        {nudge && (
          <div className="absolute bottom-full left-0 mb-3 flex w-max max-w-[16rem] items-start gap-2 rounded-2xl border border-border bg-bg p-3 shadow-xl ring-1 ring-brand/10 duration-300 animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none">
            <button type="button" onClick={() => { dismissNudge(); openPanel(); }} className="text-start text-xs font-medium text-fg">
              {t('ai.nudge', { defaultValue: '👋 Ask me anything about the store' })}
            </button>
            <button type="button" onClick={dismissNudge} className="shrink-0 rounded p-0.5 text-fg-muted transition hover:text-fg" aria-label={t('ai.close', { defaultValue: 'Close' })}>
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
        <span className="sf-ring pointer-events-none absolute inset-0 rounded-full bg-brand" aria-hidden />
        <button
          type="button"
          onClick={() => { dismissNudge(); openPanel(); }}
          aria-label={t('ai.launcher', { defaultValue: 'Shop with AI' })}
          className="relative inline-flex items-center gap-2 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-brand-contrast shadow-2xl shadow-brand/30 ring-2 ring-brand/20 transition hover:scale-105 active:scale-95 motion-reduce:transform-none"
        >
          <Sparkles className="sf-float h-5 w-5" aria-hidden />
          <span className="hidden sm:inline">{t('ai.launcher', { defaultValue: 'Shop with AI' })}</span>
        </button>
      </div>
    );
  }

  const panel = (
    <div
      className={`flex flex-col overflow-hidden rounded-2xl border border-brand/30 bg-bg shadow-2xl shadow-brand/10 ring-1 ring-brand/20 ${
        full ? 'mx-auto h-full w-full max-w-3xl' : 'h-[34rem] w-[min(23rem,calc(100vw-2rem))]'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-border bg-gradient-to-b from-brand/[0.10] to-transparent px-3 py-2.5">
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand/15 text-brand ring-1 ring-brand/20">
          {site.logoUrl ? (
            <img src={assetUrl(site.logoUrl) ?? undefined} alt="" className="h-full w-full object-cover" />
          ) : (
            <Sparkles className="h-4 w-4" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold leading-tight">{site.storeName}</p>
          <p className="flex items-center gap-1.5 truncate text-[0.7rem] text-fg-muted">
            <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-success" aria-hidden />
            {t('ai.subtitle', { defaultValue: 'AI shopping assistant' })}
          </p>
        </div>
        {messages.length > 0 && (
          <button type="button" onClick={clearChat} className="rounded-lg p-1.5 text-fg-muted transition hover:bg-brand/10 hover:text-fg" aria-label={t('ai.clear', { defaultValue: 'Clear conversation' })} title={t('ai.clear', { defaultValue: 'Clear conversation' })}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        )}
        <button type="button" onClick={() => { window.clearTimeout(closeTimer.current); setClosing(false); setFull((f) => !f); }} className="rounded-lg p-1.5 text-fg-muted transition hover:bg-brand/10 hover:text-fg" aria-label={full ? t('ai.exitFullscreen', { defaultValue: 'Exit full screen' }) : t('ai.fullscreen', { defaultValue: 'Full screen' })} title={full ? t('ai.exitFullscreen', { defaultValue: 'Exit full screen' }) : t('ai.fullscreen', { defaultValue: 'Full screen' })}>
          {full ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
        </button>
        <button type="button" onClick={requestClose} className="rounded-lg p-1.5 text-fg-muted transition hover:bg-brand/10 hover:text-fg" aria-label={t('ai.close', { defaultValue: 'Close' })} title={t('ai.close', { defaultValue: 'Close' })}>
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {/* Messages */}
      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} onScroll={onScroll} className="h-full overflow-y-auto px-3 py-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center px-1 py-6 text-center duration-300 animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none">
              <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-brand/15 text-brand ring-1 ring-brand/20">
                {site.logoUrl ? <img src={assetUrl(site.logoUrl) ?? undefined} alt="" className="h-full w-full object-cover" /> : <Sparkles className="sf-float h-7 w-7" aria-hidden />}
              </span>
              <p className="mt-3 text-base font-bold">{t('ai.welcome', { defaultValue: 'Hi! What are you looking for?' })}</p>
              <p className="mt-1 max-w-[17rem] text-xs leading-relaxed text-fg-muted">{t('ai.welcomeHint', { defaultValue: 'Ask me to find products, compare them, or recommend something — I know the whole store.' })}</p>
              <p className="mt-5 mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-fg-subtle">{t('ai.tryAsking', { defaultValue: 'Try asking' })}</p>
              <div className="flex w-full max-w-xs flex-col gap-2">
                {suggestions.map(({ icon: Icon, label }, si) => (
                  <button
                    key={label}
                    type="button"
                    disabled={loading}
                    onClick={() => void send(label)}
                    style={{ animationDelay: `${si * 50}ms` }}
                    className="group flex items-center gap-2.5 rounded-xl border border-border bg-bg-subtle px-3 py-2.5 text-start text-sm font-medium text-fg transition duration-300 animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-backwards hover:-translate-y-0.5 hover:border-brand/40 hover:bg-brand/5 hover:shadow-sm active:scale-[0.99] disabled:opacity-50 motion-reduce:transform-none motion-reduce:animate-none"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand transition group-hover:bg-brand/15">
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-fg-subtle transition group-hover:text-brand rtl:-scale-x-100" aria-hidden />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col">
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const sameRun = !m.error && prev?.role === m.role && !prev.error;
              const isLast = i === messages.length - 1;
              const revealing = isLast && revealChars !== null && m.role === 'assistant' && !m.error;
              const showChips = isLast && m.role === 'assistant' && !m.error && !loading && revealChars === null;
              return (
                <div key={i} className={`${i > 0 ? (sameRun ? 'mt-1' : 'mt-3') : ''} ${isLast ? 'duration-200 animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none' : ''}`}>
                  {m.error ? (
                    <div className="me-auto flex w-fit max-w-[85%] items-start gap-2 rounded-2xl rounded-es-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-fg shadow-sm">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
                      <span className="min-w-0">
                        <span className="block">{m.content}</span>
                        <button type="button" onClick={retry} disabled={loading} className="mt-2 inline-flex items-center gap-1 rounded-full border border-warning/50 px-2.5 py-0.5 text-xs font-semibold text-fg transition hover:bg-warning/15 disabled:opacity-50">
                          {t('ai.retry', { defaultValue: 'Retry' })}
                        </button>
                      </span>
                    </div>
                  ) : (
                    <>
                      {m.content && (m.role === 'user' ? (
                        <div className="ms-auto w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-ee-md bg-brand px-3.5 py-2 text-sm text-brand-contrast shadow-sm shadow-brand/20">{m.content}</div>
                      ) : (
                        <div className="flex items-start gap-2">
                          {sameRun ? (
                            <span className="w-6 shrink-0" aria-hidden />
                          ) : (
                            <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand ring-1 ring-brand/20">
                              <Sparkles className="h-3.5 w-3.5" aria-hidden />
                            </span>
                          )}
                          <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl border border-border bg-bg-subtle px-3.5 py-2 text-sm text-fg shadow-sm ${sameRun ? '' : 'rounded-es-md'}`}>
                            {renderAiText(revealing ? m.content.slice(0, revealChars ?? 0) : m.content)}
                          </div>
                        </div>
                      ))}
                      {m.products && m.products.length > 0 && (
                        <div className={`ms-8 mt-2 grid gap-2 ${full ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1'}`}>
                          {m.products.map((p, pi) => {
                            const added = addedId === p.id;
                            const saving = resolveSaving(p);
                            return (
                              <div
                                key={p.id}
                                style={{ animationDelay: `${pi * 60}ms` }}
                                className="flex items-center gap-2.5 rounded-xl border border-border bg-bg p-2 transition duration-300 animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-backwards hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md motion-reduce:animate-none motion-reduce:transform-none"
                              >
                                <button type="button" onClick={() => navigate(`/s/${slug}/product/${p.slug ?? p.id}`)} className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-bg-muted" aria-label={p.name}>
                                  {p.imageUrl ? <img src={assetUrl(p.imageUrl) ?? undefined} alt={p.name} className="h-full w-full object-contain" /> : <ShoppingBag className="h-6 w-6 text-fg-subtle" aria-hidden />}
                                </button>
                                <div className="min-w-0 flex-1">
                                  <button type="button" onClick={() => navigate(`/s/${slug}/product/${p.slug ?? p.id}`)} className="block truncate text-start text-xs font-semibold leading-snug transition-colors hover:text-brand">{p.name}</button>
                                  <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 font-mono text-sm font-bold tabular-nums text-brand">
                                    {money(p)}
                                    {saving && <OriginalPrice amount={saving.originalPrice} currency={p.currencyCode} className="text-xs font-normal text-fg-subtle" />}
                                  </p>
                                  {!p.inStock && (
                                    <span className="mt-1 inline-block rounded-full bg-danger/10 px-1.5 py-0.5 text-[0.65rem] font-semibold leading-none text-danger">
                                      {t('ai.outOfStock', { defaultValue: 'Out of stock' })}
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addToCart(p)}
                                  disabled={!p.inStock}
                                  className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-sm transition ${added ? 'bg-success text-fg-inverse' : 'bg-brand text-brand-contrast hover:scale-105 hover:opacity-90 active:scale-95'} disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transform-none`}
                                  aria-label={added ? t('ai.added', { defaultValue: 'Added to cart' }) : t('ai.add', { defaultValue: 'Add to cart' })}
                                  title={added ? t('ai.added', { defaultValue: 'Added to cart' }) : t('ai.add', { defaultValue: 'Add to cart' })}
                                >
                                  {added ? <Check className="sf-pop h-4 w-4" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {m.orderNumber && (
                        <div className="ms-8 mt-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/s/${slug}/order/${m.orderNumber}`)}
                            className="flex w-full items-center gap-2.5 rounded-xl border border-success/40 bg-success/10 p-3 text-start shadow-sm transition hover:-translate-y-0.5 hover:bg-success/15 hover:shadow-md motion-reduce:transform-none"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success/20 text-success"><Check className="h-5 w-5" aria-hidden /></span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold">{t('ai.orderPlaced', { defaultValue: 'Order placed!' })}</span>
                              <span className="block truncate text-xs text-fg-muted">#{m.orderNumber} · {t('ai.orderTrack', { defaultValue: 'Cash on delivery — tap to track' })}</span>
                            </span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-success rtl:-scale-x-100" aria-hidden />
                          </button>
                        </div>
                      )}
                      {showChips && (
                        <div className="ms-8 mt-2 flex flex-wrap gap-1.5 duration-200 animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none">
                          {chipsFor(m).map((c) => (
                            <button key={c} type="button" onClick={() => void send(c)} className="rounded-full border border-brand/30 px-3 py-1 text-xs text-brand transition hover:bg-brand/10">{c}</button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {loading && (
              <div className={`${messages.length > 0 ? 'mt-3' : ''} flex items-start gap-2 duration-200 animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none`} role="status" aria-label={t('ai.thinking', { defaultValue: 'Thinking…' })}>
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand ring-1 ring-brand/20">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                </span>
                {streamText ? (
                  <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-es-md border border-border bg-bg-subtle px-3.5 py-2 text-sm text-fg shadow-sm">
                    {renderAiText(streamText)}
                    <span className="ms-0.5 inline-block h-3.5 w-0.5 animate-pulse rounded bg-brand align-middle motion-reduce:animate-none" aria-hidden />
                  </div>
                ) : (
                  <span className="flex h-9 items-center gap-1 rounded-2xl rounded-es-md border border-border bg-bg-subtle px-3 shadow-sm">
                    {[0, 120, 240].map((d) => (
                      <span key={d} className="sf-typing h-1.5 w-1.5 rounded-full bg-fg-muted" style={{ ['--sf-delay']: `${d}ms` } as CSSProperties} />
                    ))}
                    <span className="ms-1.5 text-xs text-fg-muted duration-200 animate-in fade-in-0 motion-reduce:animate-none">
                      {activity === 'searching'
                        ? t('ai.actSearching', { defaultValue: 'Searching the store…' })
                        : activity === 'product'
                          ? t('ai.actProduct', { defaultValue: 'Checking a product…' })
                          : activity === 'ordering'
                            ? t('ai.actOrdering', { defaultValue: 'Placing your order…' })
                            : t('ai.typing', { defaultValue: 'Typing…' })}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {showNewReply && (
          <button
            type="button"
            onClick={() => { scrollToBottom(); setShowNewReply(false); }}
            className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-brand/30 bg-bg px-3 py-1 text-xs font-semibold text-brand shadow-lg transition duration-200 animate-in fade-in-0 slide-in-from-bottom-2 hover:bg-brand/10 motion-reduce:animate-none"
          >
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />{t('ai.newReply', { defaultValue: 'New reply' })}
          </button>
        )}
      </div>

      {/* Cart shortcut + input */}
      <form
        onSubmit={(e) => { e.preventDefault(); void send(input); }}
        className="flex items-end gap-2 border-t border-border bg-gradient-to-t from-brand/[0.05] to-transparent px-3 py-2.5"
      >
        <button type="button" onClick={() => navigate(`/s/${slug}/cart`)} className="rounded-lg p-2 text-fg-muted transition hover:bg-brand/10 hover:text-brand" aria-label={t('ai.cart', { defaultValue: 'Cart' })} title={t('ai.cart', { defaultValue: 'Cart' })}>
          <ShoppingBag className="h-5 w-5" aria-hidden />
        </button>
        <textarea
          ref={inputRef}
          value={input}
          rows={1}
          onChange={(e) => { setInput(e.target.value); autoGrow(e.currentTarget); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(input); }
          }}
          placeholder={t('ai.placeholder', { defaultValue: 'Ask for anything…' })}
          className="max-h-24 min-w-0 flex-1 resize-none rounded-xl border border-border bg-bg px-3 py-2 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
          maxLength={4000}
        />
        {loading ? (
          <button type="button" onClick={stop} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-bg-subtle text-fg transition-all duration-200 hover:border-brand/40 hover:text-brand active:scale-95" aria-label={t('ai.stop', { defaultValue: 'Stop' })} title={t('ai.stop', { defaultValue: 'Stop' })}>
            <Square className="h-3 w-3 fill-current" aria-hidden />
          </button>
        ) : (
          <button type="submit" disabled={!input.trim()} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand text-brand-contrast transition-all duration-200 hover:opacity-90 active:scale-95 disabled:scale-90 disabled:opacity-40" aria-label={t('ai.send', { defaultValue: 'Send' })}>
            <Send className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
          </button>
        )}
      </form>
    </div>
  );

  // ---- Full-screen mode: dim + blur backdrop, centered panel ----
  if (full) {
    return (
      <div role="dialog" aria-modal="true" aria-label={t('ai.subtitle', { defaultValue: 'AI shopping assistant' })} className="fixed inset-0 z-[70] flex items-stretch justify-center bg-slate-950/70 p-3 backdrop-blur-md duration-200 animate-in fade-in-0 sm:p-6" onClick={() => setFull(false)}>
        {panel}
      </div>
    );
  }

  // ---- Docked bottom-left ----
  return (
    <div
      className={`fixed bottom-4 left-4 z-50 origin-bottom-left motion-reduce:animate-none ${
        closing
          ? 'duration-150 animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-4'
          : 'duration-300 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4'
      }`}
    >
      {panel}
    </div>
  );
}
